import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { UserType } from '@api/Users/User';
import UserService from '@api/Users/UserCtrl';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';

interface ModalResetPinProps {
    open: boolean;
    onClose: () => void;
    user: Pick<UserType, 'uid' | 'email' | 'profile'>;
}

/**
 * Restablecer el PIN de alguien que lo olvidó (2026-09-29). Quien lo administra le
 * pone uno nuevo y se lo dice en persona; el correo con el que entra no cambia.
 */
export const ModalResetPin: React.FC<ModalResetPinProps> = ({ open, onClose, user }) => {
    const userService = useMemo(() => new UserService(), []);
    const [pin, setPin] = useState('');
    const [confirmation, setConfirmation] = useState('');

    useEffect(() => {
        if (open) {
            setPin('');
            setConfirmation('');
        }
    }, [open]);

    const valid = /^\d{6}$/.test(pin);
    const mismatch = confirmation.length === 6 && confirmation !== pin;

    const onSave = async (event: React.FormEvent) => {
        event.preventDefault();
        showLoader();
        try {
            await userService.onResetPin(user, pin);
            showAlertMessage(`Listo: ${user.profile?.name || 'el usuario'} ya entra con el PIN nuevo`);
            onClose();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const pinInput = { maxLength: 6, inputMode: 'numeric' as const, pattern: '[0-9]*' };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ style: { borderRadius: 15 } }}>
            <form onSubmit={onSave}>
                <DialogTitle fontWeight={600}>Restablecer PIN</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1}>
                        <Typography variant="body2" color="text.secondary">
                            {`Sigue entrando con ${user.email}. Dile el PIN nuevo en persona; después puede cambiarlo en Mi perfil.`}
                        </Typography>
                        <TextField
                            autoFocus
                            required
                            fullWidth
                            type="password"
                            label="PIN nuevo (6 dígitos)"
                            autoComplete="new-password"
                            value={pin}
                            onChange={(event) => setPin(event.target.value.replace(/\D/g, ''))}
                            inputProps={pinInput}
                        />
                        <TextField
                            required
                            fullWidth
                            type="password"
                            label="Repite el PIN"
                            autoComplete="new-password"
                            value={confirmation}
                            onChange={(event) => setConfirmation(event.target.value.replace(/\D/g, ''))}
                            error={mismatch}
                            helperText={mismatch ? 'No coincide' : ' '}
                            inputProps={pinInput}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancelar</Button>
                    <Button type="submit" variant="contained" color="primary" disabled={!valid || confirmation !== pin}>
                        Restablecer
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
